import React, { useEffect, useState } from 'react'
import Layout from '../components/layout'
import Seo from '../components/seo'

const VerifyCertificate = () => {
    const [certificateId, setCertificateId] = useState('')
    const [fullName, setFullName] = useState('')
    const [result, setResult] = useState(null)
    const [submitted, setSubmitted] = useState(false)
    const [loading, setLoading] = useState(false)

    const isExpired = expiryDate => {
        const today = new Date()
        const todayDateOnly = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
        )
        const expiry = new Date(expiryDate)
        const expiryDateOnly = new Date(
            expiry.getFullYear(),
            expiry.getMonth(),
            expiry.getDate()
        )
        return expiryDateOnly < todayDateOnly
    }

    const verifyCertificate = async (certId, name) => {
        setSubmitted(true)
        setLoading(true)

        try {
            const response = await fetch(
                'https://qtafgbejdliuhrkijdjx.supabase.co/functions/v1/user-certificates',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ cert_id: certId, name: name }),
                }
            )

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error(
                        'Certificate not found. Please check the details.'
                    )
                }
                if (response.status === 500) {
                    throw new Error('❌ Server error. Please try again later.')
                }
                throw new Error('❌ An error occurred. Please try again later.')
            }

            const data = await response.json()

            if (data.status === 'success') {
                const expired = isExpired(data.data.expires_at)

                setResult({
                    success: true,
                    expired,
                    message: expired
                        ? '⚠️ Certificate is verified but has expired.'
                        : '✅ Certificate is valid and verified!',
                    certId: data.data.cert_id,
                    name: data.data.name,
                    issueDate: data.data.issued_at,
                    expiryDate: data.data.expires_at,
                })
            } else {
                setResult({
                    success: false,
                    message:
                        '❌ Certificate not found. Please check the details.',
                })
            }
        } catch (error) {
            console.error('Error verifying certificate:', error)
            setResult({
                success: false,
                message:
                    error.message ||
                    '❌ An error occurred while verifying the certificate. Please try again later.',
            })
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async e => {
        e.preventDefault()

        verifyCertificate(certificateId, fullName)
    }

    const handleRetry = () => {
        setSubmitted(false)
        setResult(null)
        setCertificateId('')
        setFullName('')
    }

    useEffect(() => {
        const location = window.location.href
        const params = new URLSearchParams(location.split('?')[1])
        const certId = params.get('cert_id')
        const name = params.get('name')

        if (certId && name) {
            setCertificateId(certId)
            setFullName(name)
            verifyCertificate(certId, name)
        }
    }, [])

    return (
        <Layout>
            <Seo
                title="Verify Certificate"
                description="Verify your certificate from Ricta Technologies"
            />
            <div className="flex justify-center items-center flex-1 bg-gray-100 py-8">
                <div className="max-w-xl w-full p-8 bg-white rounded-lg shadow-xl">
                    <h1 className="text-3xl font-semibold text-center text-gray-800 mb-6">
                        Verify Certificate
                    </h1>

                    {!submitted && (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Certificate ID
                                </label>
                                <input
                                    type="text"
                                    placeholder="CWFE-00000"
                                    value={certificateId}
                                    onChange={e =>
                                        setCertificateId(e.target.value)
                                    }
                                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-mainColor focus:border-mainColor"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Name
                                </label>
                                <input
                                    type="text"
                                    placeholder="John Doe"
                                    value={fullName}
                                    onChange={e => setFullName(e.target.value)}
                                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-mainColor focus:border-mainColor"
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full bg-mainColor text-white py-2 px-4 rounded hover:opacity-90 transition duration-200"
                            >
                                Verify
                            </button>
                        </form>
                    )}

                    {loading && (
                        <div className="mt-6 text-center text-gray-600">
                            🔄 Verifying certificate, please wait...
                        </div>
                    )}

                    {submitted && result && !loading && (
                        <div
                            className={`mt-6 p-6 rounded-lg text-center border-2 ${
                                result.success
                                    ? result.expired
                                        ? 'bg-yellow-100 border-yellow-500'
                                        : 'bg-green-100 border-green-500'
                                    : 'bg-red-100 border-red-500'
                            }`}
                        >
                            <p className="text-lg font-semibold text-gray-800">
                                {result.message}
                            </p>

                            {result.success && (
                                <div className="mt-4 text-sm text-gray-600">
                                    <p>
                                        <strong>Certificate ID:</strong>{' '}
                                        {result.certId}
                                    </p>
                                    <p>
                                        <strong>Name:</strong> {result.name}
                                    </p>
                                    <p>
                                        <strong>Issue Date:</strong>{' '}
                                        {new Date(
                                            result.issueDate
                                        ).toLocaleDateString()}
                                    </p>
                                    <p>
                                        <strong>Expiry Date:</strong>{' '}
                                        {new Date(
                                            result.expiryDate
                                        ).toLocaleDateString()}
                                    </p>
                                </div>
                            )}

                            {!result.success && (
                                <button
                                    onClick={handleRetry}
                                    className="mt-4 inline-block bg-white text-red-600 font-semibold py-2 px-4 border border-red-600 rounded hover:bg-red-50 transition"
                                >
                                    Retry
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    )
}

export default VerifyCertificate
